import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
export function FeetAnklesHeroSection() {
  return <section className="relative w-full py-12 sm:py-20 md:py-24 lg:py-12 overflow-hidden" style={{
    backgroundColor: '#FFFFFF'
  }} data-id="element-820">
      {/* Background gradient fade to bottom */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'linear-gradient(to bottom, transparent 0%, #FFFFFF 100%)'
    }} data-id="element-821" />

      {/* Background subtle gradient */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-ginger-light/5 to-transparent pointer-events-none" data-id="element-822" />

      {/* Decorative blur element */}
      <div className="absolute -top-20 -right-20 w-96 h-96 bg-ginger/5 rounded-full blur-3xl pointer-events-none" data-id="element-823" />

      <div className="max-w-7xl mx-auto px-6 w-full grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center" data-id="element-824">
        {/* Text Content */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex flex-col items-center sm:items-start text-center sm:text-left z-10" data-id="element-825">
          <div className="inline-block px-4 py-1.5 mb-6 sm:mb-8 border border-ginger/20 rounded-full bg-white/50 backdrop-blur-sm shadow-sm mx-auto sm:mx-0" data-id="element-826">
            <span className="text-ginger font-medium text-xs tracking-[0.3em] uppercase" data-id="element-827">
              America's #1 Ginger Oil
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-charcoal leading-[1.1] mb-4 sm:mb-6 tracking-tight" data-id="element-828">
            By Evening, Your Feet & Ankles Aren't Sore{' '}
            <span className="text-ginger font-normal block mt-2" data-id="element-829">
              They're Holding Pressure
            </span>
          </h1>

          <p className="text-sm sm:text-base lg:text-lg text-charcoal-light/90 mb-6 sm:mb-8 leading-relaxed font-light max-w-xl mx-auto sm:mx-0" data-id="element-830">
            Tight shoes, swollen ankles, and aching feet are signs circulation
            has slowed. The right warmth helps your body release pressure — not
            freeze it.
          </p>

          {/* Hero Image - Mobile Only */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.1,
          ease: 'easeOut'
        }} className="relative w-full mb-6 sm:mb-8 lg:hidden" data-id="element-831">
            <div className="relative w-full overflow-hidden rounded-2xl shadow-2xl shadow-ginger/10" data-id="element-832">
              <img src="https://cdn.magicpatterns.com/uploads/avxXdHm973XVSypaF5JSKo/hero_img_3_mobile.png" alt="GINEXT Ginger Oil with circulation benefits" className="w-full h-auto" loading="eager" data-id="element-833" />
            </div>
          </motion.div>

          <div className="w-full sm:w-auto lg:w-full flex justify-center sm:justify-start" data-id="element-834">
            <a href="#bundles" className="block scroll-smooth w-full sm:w-auto lg:w-full" data-id="element-835">
              <motion.div whileHover={{
              scale: 1.02,
              boxShadow: '0 20px 40px -10px rgba(232, 119, 34, 0.3)'
            }} whileTap={{
              scale: 0.98
            }} className="w-full sm:w-auto lg:w-full group bg-ginger text-white px-8 sm:px-10 lg:px-12 py-4 sm:py-5 lg:py-6 rounded-xl font-normal text-base sm:text-lg lg:text-xl shadow-xl shadow-ginger/20 transition-all duration-300" data-id="element-836">
                <div className="flex items-center justify-center gap-3 mb-1" data-id="element-837">
                  <span data-id="element-838">Try GINEXT Risk-Free</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 group-hover:translate-x-1 transition-transform" data-id="element-839" />
                </div>
                <p className="text-xs sm:text-sm lg:text-base font-light text-white/90 text-center tracking-wide" data-id="element-840">
                  30 Days Money Back Guaranteed
                </p>
              </motion.div>
            </a>
          </div>

          {/* Stars and Badge - Side by Side */}
          <div className="mt-6 sm:mt-8 flex items-center justify-center sm:justify-start gap-4 w-full" data-id="element-841">
            {/* 5 Stars */}
            <div className="flex gap-1" data-id="element-842">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 fill-ginger text-ginger" data-id="element-843" />)}
            </div>

            {/* Badge Image */}
            <img src="https://cdn.magicpatterns.com/uploads/iTBEeQnf6RsENHtUPEU6Sy/BADGE.png" alt="Trusted by 10,000+ happy customers" className="h-10 sm:h-12 lg:h-14 w-auto opacity-90 hover:opacity-100 transition-opacity" data-id="element-844" />
          </div>
        </motion.div>

        {/* Desktop Hero Image */}
        <motion.div initial={{
        opacity: 0,
        x: 30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.9,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="hidden lg:block relative" data-id="element-845">
          <div className="relative w-full overflow-hidden rounded-3xl shadow-2xl shadow-ginger/20" data-id="element-846">
            <img src="https://cdn.magicpatterns.com/uploads/avxXdHm973XVSypaF5JSKo/hero_img_3_mobile.png" alt="GINEXT Ginger Oil - Natural circulation support" className="w-full h-auto transform hover:scale-105 transition-transform duration-700" loading="eager" data-id="element-847" />
          </div>

          {/* Floating accent element */}
          <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-ginger/10 rounded-full blur-2xl pointer-events-none" data-id="element-848" />
        </motion.div>
      </div>
    </section>;
}